import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import PublicSeo from "@/components/PublicSeo";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import GlobalUpgradeBar from "@/components/GlobalUpgradeBar";
import TrialOfferPopup from "@/components/TrialOfferPopup";
import { FootballPromotionProvider } from "@/contexts/FootballPromotionContext";
import FootballWeekendBanner from "@/components/FootballWeekendBanner";

// The verify-email, reset-password, and magic-login links all hit real backend
// routes now (see backend/routers/auth.py) rather than this app's client-side
// pages directly, because a fresh browser navigation straight to a client-side
// route depends on the static host falling back to index.html for unknown
// paths, which isn't guaranteed everywhere this is deployed. The backend
// redirects here to "/" (which every static host serves directly) carrying
// the outcome in a query param for this to pick up once the app has actually
// loaded — from here on every further navigation is a normal client-side
// route change, not a fresh server request, so it isn't exposed to that gap.
function AuthLinkRedirectHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    const verify = searchParams.get("verify");
    const resetToken = searchParams.get("reset_token");
    const magic = searchParams.get("magic");
    if (!verify && !resetToken && !magic) return;

    if (resetToken) {
      // Reset-password can't resolve here — the new password only exists once
      // the user types it in. Just forward the token to the real client-side
      // route, which works fine now that the app is loaded and running.
      navigate(`/reset-password?token=${encodeURIComponent(resetToken)}`, { replace: true });
      return;
    }

    if (verify) {
      const reason = searchParams.get("reason");
      if (verify === "success") {
        checkAuth();
        toast.success("Email verified! Welcome to the posse.");
        navigate("/dashboard", { replace: true });
      } else if (verify === "already") {
        toast.info("That email is already verified — log in to continue.");
        navigate("/login", { replace: true });
      } else {
        const message = reason === "expired"
          ? "That verification link expired. Request a new one from the login page."
          : "That verification link is invalid or already used. Request a new one from the login page.";
        toast.error(message);
        navigate("/login", { replace: true });
      }
      return;
    }

    if (magic === "success") {
      checkAuth();
      toast.success("Welcome to the posse!");
      navigate("/dashboard", { replace: true });
    } else {
      toast.error("That invite link is invalid or already used. Ask the admin for a new one.");
      navigate("/login", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return null;
}

// Route-level code splitting: each page ships as its own chunk, fetched only
// when its route is visited, instead of one bundle carrying every page.
const FootballWeekendPage = lazy(() => import("@/pages/FootballWeekendPage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ParlayBuilderPage = lazy(() => import("@/pages/ParlayBuilderPage"));
const MyParlaysPage = lazy(() => import("@/pages/MyParlaysPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const MarketplacePage = lazy(() => import("@/pages/MarketplacePage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const ReferralsPage = lazy(() => import("@/pages/ReferralsPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const PickHistoryPage = lazy(() => import("@/pages/PickHistoryPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminRevenuePage = lazy(() => import("@/pages/AdminRevenuePage"));
const PublicParlayPage = lazy(() => import("@/pages/PublicParlayPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const MagicLoginPage = lazy(() => import("@/pages/MagicLoginPage"));
const CrapsCoachPage = lazy(() => import("@/pages/CrapsCoachPage"));
const CrapsTablePage = lazy(() => import("@/pages/CrapsTablePage"));
const PokerTrainerPage = lazy(() => import("@/pages/PokerTrainerPage"));
const BlackjackTrainerPage = lazy(() => import("@/pages/BlackjackTrainerPage"));
const ChampionsPage = lazy(() => import("@/pages/ChampionsPage"));
const SportLandingPage = lazy(() => import("@/pages/SportLandingPage"));
const LiveTrackerPage = lazy(() => import("@/pages/LiveTrackerPage"));
const StatsPage = lazy(() => import("@/pages/StatsPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const ResponsiblePlayPage = lazy(() => import("@/pages/ResponsiblePlayPage"));
const WalletPage = lazy(() => import("@/pages/WalletPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));

function AppLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A]">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      {/* Bottom padding so the GlobalUpgradeBar never covers the footer */}
      <div className="h-20 sm:h-24" aria-hidden="true" />
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF6B1A] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <PublicSeo />
        <AuthProvider>
          <FootballPromotionProvider>
          <FootballWeekendBanner />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#111115',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
              },
            }}
          />
          <PWAInstallPrompt />
          <GlobalUpgradeBar />
          <TrialOfferPopup />
          <AuthLinkRedirectHandler />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<AppLayout><LandingPage /></AppLayout>} />
              <Route path="/football-month" element={<AppLayout><FootballWeekendPage /></AppLayout>} />
              <Route path="/football-weekend" element={<AppLayout><FootballWeekendPage /></AppLayout>} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/magic" element={<MagicLoginPage />} />
              <Route path="/craps" element={<AppLayout><CrapsTablePage /></AppLayout>} />
              <Route path="/craps/coach" element={<AppLayout><CrapsCoachPage /></AppLayout>} />
              <Route path="/poker" element={<AppLayout><PokerTrainerPage /></AppLayout>} />
              <Route path="/blackjack" element={<AppLayout><BlackjackTrainerPage /></AppLayout>} />
              <Route path="/champions" element={
                <ProtectedRoute>
                  <AppLayout><ChampionsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/parlay/:parlayId" element={<PublicParlayPage />} />
              <Route path="/sports/:sportSlug" element={<SportLandingPage />} />
              <Route path="/terms" element={<AppLayout><TermsPage /></AppLayout>} />
              <Route path="/privacy" element={<AppLayout><PrivacyPage /></AppLayout>} />
              <Route path="/responsible-play" element={<AppLayout><ResponsiblePlayPage /></AppLayout>} />
              <Route path="/live" element={
                <AppLayout><LiveTrackerPage /></AppLayout>
              } />
              <Route path="/leaderboard" element={
                <AppLayout><LeaderboardPage /></AppLayout>
              } />
              <Route path="/pick-history" element={
                <AppLayout><PickHistoryPage /></AppLayout>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout><DashboardPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/builder" element={
                <ProtectedRoute>
                  <AppLayout><ParlayBuilderPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/my-parlays" element={
                <ProtectedRoute>
                  <AppLayout><MyParlaysPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/stats" element={
                <ProtectedRoute>
                  <AppLayout><StatsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pricing" element={
                <ProtectedRoute>
                  <AppLayout><PricingPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/marketplace" element={
                <ProtectedRoute>
                  <AppLayout><MarketplacePage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/referrals" element={
                <ProtectedRoute>
                  <AppLayout><ReferralsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/wallet" element={
                <ProtectedRoute>
                  <AppLayout><WalletPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <AppLayout><SettingsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AppLayout><AdminPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/revenue" element={
                <ProtectedRoute>
                  <AppLayout><AdminRevenuePage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/payment-success" element={
                <ProtectedRoute>
                  <AppLayout><PaymentSuccessPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/parlay/:parlayId" element={<PublicParlayPage />} />
              <Route path="/sports/:sportSlug" element={<SportLandingPage />} />
              <Route path="/terms" element={<AppLayout><TermsPage /></AppLayout>} />
              <Route path="/privacy" element={<AppLayout><PrivacyPage /></AppLayout>} />
              <Route path="/responsible-play" element={<AppLayout><ResponsiblePlayPage /></AppLayout>} />
              <Route path="/live" element={
                <AppLayout><LiveTrackerPage /></AppLayout>
              } />
              <Route path="/leaderboard" element={
                <AppLayout><LeaderboardPage /></AppLayout>
              } />
              <Route path="/pick-history" element={
                <AppLayout><PickHistoryPage /></AppLayout>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout><DashboardPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/builder" element={
                <ProtectedRoute>
                  <AppLayout><ParlayBuilderPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/my-parlays" element={
                <ProtectedRoute>
                  <AppLayout><MyParlaysPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/stats" element={
                <ProtectedRoute>
                  <AppLayout><StatsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/pricing" element={
                <ProtectedRoute>
                  <AppLayout><PricingPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/marketplace" element={
                <ProtectedRoute>
                  <AppLayout><MarketplacePage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/referrals" element={
                <ProtectedRoute>
                  <AppLayout><ReferralsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/wallet" element={
                <ProtectedRoute>
                  <AppLayout><WalletPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <AppLayout><SettingsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AppLayout><AdminPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/revenue" element={
                <ProtectedRoute>
                  <AppLayout><AdminRevenuePage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/payment-success" element={
                <ProtectedRoute>
                  <AppLayout><PaymentSuccessPage /></AppLayout>
                </ProtectedRoute>
              } />
            </Routes>
          </Suspense>
        </FootballPromotionProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;


